import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, type HorecaTaxRowDto } from "@finora/api-client";
import { Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const inputCls = "h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const fmt = (n: number) => n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Belgrade" });
const monthStart = () => today().slice(0, 8) + "01";
const METHODS: Record<string, string> = { CASH: "Gotovina", CARD: "Kartica", BANK_TRANSFER: "Virman", VOUCHER: "Vaučer", COMBINED: "Kombinovano", INSTANT_PAYMENT: "Instant plaćanje", CHECK: "Ček", OTHER: "Drugo" };

function errMsg(err: unknown) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return "Učitavanje nije uspelo.";
}

function TaxCells({ rows }: { rows: HorecaTaxRowDto[] }) {
  if (!rows.length) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5">
      {rows.map((r) => (
        <div key={r.label + r.rate} className="whitespace-nowrap text-xs">
          {r.label}: osnovica {fmt(r.base)} · PDV {fmt(r.tax)}
        </div>
      ))}
    </div>
  );
}

export function HorecaKnjigePage() {
  const [tab, setTab] = useState<"kep" | "dpu">("kep");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [date, setDate] = useState(today());
  const [loc, setLoc] = useState("");

  const locations = useQuery({ queryKey: ["horeca-locations"], queryFn: () => api.listBusinessLocationOptions(), retry: false });
  const businessLocationId = loc || undefined;
  const kep = useQuery({
    queryKey: ["horeca-kep", from, to, loc],
    queryFn: () => api.getHorecaKep({ from, to, businessLocationId }),
    enabled: tab === "kep" && !!from && !!to,
    retry: false,
  });
  const dpu = useQuery({
    queryKey: ["horeca-dpu", date, loc],
    queryFn: () => api.getHorecaDpu({ date, businessLocationId }),
    enabled: tab === "dpu" && !!date,
    retry: false,
  });
  const active = tab === "kep" ? kep : dpu;

  if (active.isError && active.error instanceof ApiError && active.error.response.errorCode === "MODULE_NOT_AVAILABLE") {
    return <ModuleGateNotice error={active.error} moduleKey="horeca" />;
  }

  return (
    <div className="print-area space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">HoReCa knjige</h1>
          <p className="text-sm text-slate-500">KEP (knjiga evidencije prometa) i DPU (dnevni pazar) iz izdatih fiskalnih računa.</p>
        </div>
        <button className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-slate-50 print:hidden" onClick={() => window.print()}>
          Štampaj
        </button>
      </div>

      <p className="hidden text-sm print:block">
        {tab === "kep" ? `Period: ${from.split("-").reverse().join(".")}. – ${to.split("-").reverse().join(".")}.` : `Dan: ${date.split("-").reverse().join(".")}.`}
      </p>
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div className="flex rounded-lg border border-border p-0.5">
          {(["kep", "dpu"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={"rounded-md px-4 py-1.5 text-sm " + (tab === t ? "bg-brand text-white" : "text-slate-600")}>
              {t === "kep" ? "KEP" : "DPU"}
            </button>
          ))}
        </div>
        {tab === "kep" ? (
          <>
            <label className="text-xs text-slate-500">Od<input className={inputCls + " block"} type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="text-xs text-slate-500">Do<input className={inputCls + " block"} type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          </>
        ) : (
          <label className="text-xs text-slate-500">Dan<input className={inputCls + " block"} type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        )}
        {(locations.data?.length ?? 0) > 1 && (
          <label className="text-xs text-slate-500">
            Poslovni prostor
            <select className={inputCls + " block"} value={loc} onChange={(e) => setLoc(e.target.value)}>
              <option value="">Svi</option>
              {locations.data?.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {active.isLoading && <Skeleton className="h-40 w-full" />}
      {active.isError && !(active.error instanceof ApiError && active.error.response.errorCode === "MODULE_NOT_AVAILABLE") && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(active.error)}</p>
      )}

      {tab === "kep" && kep.data && (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2">R.br.</th>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2 text-right">Računa</th>
                <th className="px-3 py-2">Po poreskim stopama</th>
                <th className="px-3 py-2 text-right">Prodaja</th>
                <th className="px-3 py-2 text-right">Storno</th>
                <th className="px-3 py-2 text-right">Promet</th>
                <th className="px-3 py-2 text-right">Ukupno</th>
              </tr>
            </thead>
            <tbody>
              {kep.data.days.map((d) => (
                <tr key={d.date} className="border-t border-border align-top">
                  <td className="px-3 py-2">{d.ordinal}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{d.date.split("-").reverse().join(".")}.</td>
                  <td className="px-3 py-2 text-right">{d.receipts}{d.refunds_count ? ` (−${d.refunds_count})` : ""}</td>
                  <td className="px-3 py-2"><TaxCells rows={d.by_tax} /></td>
                  <td className="px-3 py-2 text-right">{fmt(d.sales_gross)}</td>
                  <td className="px-3 py-2 text-right">{fmt(d.refunds_gross)}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(d.turnover_gross)}</td>
                  <td className="px-3 py-2 text-right">{fmt(d.running_total)}</td>
                </tr>
              ))}
              {kep.data.days.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Nema prometa u izabranom periodu.</td></tr>
              )}
            </tbody>
            {kep.data.days.length > 0 && (
              <tfoot className="border-t-2 border-border bg-slate-50 font-medium">
                <tr>
                  <td className="px-3 py-2" colSpan={2}>Ukupno</td>
                  <td className="px-3 py-2 text-right">{kep.data.totals.receipts}</td>
                  <td className="px-3 py-2"><TaxCells rows={kep.data.totals.by_tax} /></td>
                  <td className="px-3 py-2 text-right">{fmt(kep.data.totals.sales_gross)}</td>
                  <td className="px-3 py-2 text-right">{fmt(kep.data.totals.refunds_gross)}</td>
                  <td className="px-3 py-2 text-right">{fmt(kep.data.totals.turnover_gross)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {tab === "dpu" && dpu.data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Računa", `${dpu.data.receipts}${dpu.data.refunds_count ? ` (storno ${dpu.data.refunds_count})` : ""}`],
              ["Prodaja", fmt(dpu.data.sales_gross)],
              ["Storno", fmt(dpu.data.refunds_gross)],
              ["Dnevni pazar", fmt(dpu.data.turnover_gross)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
                <div className="text-xs text-slate-500">{k}</div>
                <div className="text-xl font-semibold text-slate-900">{v}</div>
              </div>
            ))}
          </div>
          {dpu.data.first_fiscal_number && (
            <p className="text-xs text-slate-500">Prvi račun: {dpu.data.first_fiscal_number} · Poslednji: {dpu.data.last_fiscal_number}</p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="mb-2 text-sm font-medium">Po načinu plaćanja</div>
              {dpu.data.by_payment.map((p) => (
                <div key={p.method} className="flex justify-between text-sm">
                  <span>{METHODS[p.method] ?? p.method}</span>
                  <span>{fmt(p.net)}</span>
                </div>
              ))}
              {dpu.data.by_payment.length === 0 && <p className="text-sm text-slate-400">Nema prometa.</p>}
            </div>
            <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="mb-2 text-sm font-medium">Po poreskim stopama</div>
              <TaxCells rows={dpu.data.by_tax} />
            </div>
            <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="mb-2 text-sm font-medium">Po kasiru</div>
              {dpu.data.by_cashier.map((c) => (
                <div key={c.user_id ?? "?"} className="flex justify-between text-sm">
                  <span>{c.name ?? "—"} ({c.receipts})</span>
                  <span>{fmt(c.gross)}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="mb-2 text-sm font-medium">Smene</div>
              {dpu.data.shifts.map((s) => (
                <div key={s.number} className="text-sm">
                  Smena {s.number} · {s.opened_by ?? "—"} · početno {fmt(s.opening_cash)}
                  {s.counted_cash !== null ? ` · prebrojano ${fmt(s.counted_cash)}` : " · otvorena"}
                </div>
              ))}
              {dpu.data.shifts.length === 0 && <p className="text-sm text-slate-400">Nema smena.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
